import { useEffect, useMemo } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import {
	AnalysisDetailPage,
	AnalysisHiringPage,
	AnalysisListPage,
	AnalysisRequestPage,
	ClientDetailPage,
	DashboardPage,
	DetalheClientesPage,
	DetalheUsuariosPage,
	FinancialHealthListPage,
	FinancialListPage,
	ForgotPasswordPage,
	GraficosPage,
	ListagemClientesPage,
	ListagemContratosPage,
	ListagemDetalhesContratoPage,
	ListagemGeraisPage,
	ListagemRelatoriosPage,
	ListagemUsuariosPage,
	ListPermissionsPage,
	LoginPage,
	NfseListPage,
	PermissionsDetailsPage,
	ProposalDetailPage,
	ProposalListPage,
	ProposalRenovatePage,
	ResetPasswordPage,
	SignaturePage,
	SinisterDetailsPage,
	SinisterListPage,
	SinisterValuesPage,
	FirePage,
	BannerManagementPage,
	CapitalizationPage,
	IntegratorPage,
} from "../pages";

import { useDrawerContext } from "../context";
import { useProfile } from "../context/ProfileContext";
import { TransferClientsPage } from "../pages/client";
import { CreditRecoveryPage } from "../pages/creditRecovery/CreditRecoveryPage";
import { CreditRecoveryDetailsPage } from "../pages/creditRecovery/CreditRecoveryDetailsPage";
import { AgreementsPage } from "../pages/agreements/AgreementsPage";
import { AgreementsDetailsPage } from "../pages/agreements/AgreementsDetailsPage";
import { CollectionControlPage } from "../pages/collectionControl/CollectionControlPage";
import { DetalheContratosPage } from "../pages/contract";
import { DashboardGraphic } from "../pages/dashboard";
import { FinancialPage } from "../pages/finan/FinancialPage";
import { FinancialPassPage } from "../pages/finan/FinancialPassPage";
import { FinancialBorderoPage, SicoobBoletoPage, SummaryPage } from "../pages/financial";
import { FinancialInvoiceListPage } from "../pages/financial/FinancialInvoiceListPage";
import { FinancialSetupPage } from "../pages/financial/FinancialSetupPage";
import { ProccessCancelBlockClientPage } from "../pages/financial/ProccessCancelBlockClientPage";
import { OnboardPaymentPage } from "../pages/payments";
import { PaymentInvoicePage } from "../pages/PaymentsInvoice/PaymentInvoicePage";
import { PeoplePage } from "../pages/people/PeoplePage";
import { ProposalEditPage } from "../pages/proposal/ProposalEditPage";
import { FinancialSummaryPage, ReportAnalysisPage, ReportsPage, ReportUpdateProposalPage } from "../pages/relatorios";
import { ReportLifecycleAnalysisPage } from "../pages/relatorios/ReportLifecycleAnalysisPage";
import { ReportLifecycleProposalPage } from "../pages/relatorios/ReportLifecycleProposalPage";
import { ReportProposalPage } from "../pages/relatorios/ReportProposalPage";
import { ToolsPage } from "../pages/tools/ToolsPage";
import { PerfilUserDetailPresentation } from "../pages/user/components";
import { PermissionActionsEnum, PermissionRolesEnum } from "../shared/enums/profiles";
import { garanttiMenuStructure, imobMenuStructure, MenuItem } from "../shared/constants/menu";
import { PermissionRolesType } from "../shared/types/profiles/permission-roles.type";
import { checkByProfileGuard } from "../shared/guards/ProfileGuard";

const UsuariosDetalheGuard: React.FC = () => {
	const { id } = useParams<"id">();
	const isNew = id === "novo";
	const canCreate = checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Add);
	const canUpdate = checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Update);

	const canAccess = isNew ? canCreate : canUpdate;
	if (!canAccess) return <Navigate to="/dashboard" replace />;
	return <DetalheUsuariosPage />;
};

const ContratosListagemGuard: React.FC = () => {
	const { permissions } = useProfile();
	const hasContractsOptionsModule = useMemo(() => {
		return Object.prototype.hasOwnProperty.call(permissions ?? {}, PermissionRolesEnum.ContractsOptions);
	}, [permissions]);

	const canAccess = hasContractsOptionsModule
		? checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.Contracts)
		: checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts);

	if (!canAccess) return <Navigate to="/dashboard" replace />;
	return <ListagemContratosPage />;
};

const ContratosDetalhesGuard: React.FC = () => {
	const { permissions } = useProfile();
	const hasContractsOptionsModule = useMemo(() => {
		return Object.prototype.hasOwnProperty.call(permissions ?? {}, PermissionRolesEnum.ContractsOptions);
	}, [permissions]);

	const canAccess = hasContractsOptionsModule
		? checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsDetails)
		: checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts);

	if (!canAccess) return <Navigate to="/dashboard" replace />;
	return <ListagemDetalhesContratoPage />;
};

const ContratosDetalheGuard: React.FC = () => {
	const { id } = useParams<"id">();
	const isNew = id === "novo";
	const { permissions } = useProfile();
	const hasContractsOptionsModule = useMemo(() => {
		return Object.prototype.hasOwnProperty.call(permissions ?? {}, PermissionRolesEnum.ContractsOptions);
	}, [permissions]);

	const canCreate = hasContractsOptionsModule
		? checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsAdd)
		: checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsAdd);

	const canUpdate = hasContractsOptionsModule
		? checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsUpdate)
		: checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsUpdate);

	const canAccess = isNew ? canCreate : canUpdate;
	if (!canAccess) return <Navigate to="/dashboard" replace />;
	return <DetalheContratosPage />;
};
import { FireQuotePage } from "../pages/fire/quote/FireQuotePage";
import QuotationPage from "../pages/fire/Quotation/components/QuotationPresentation";
import { CapitalizationDetailPage } from "../pages/capitalization/CapitalizationDetailPage";
import { BannerProvider } from "../shared/contexts/BannerContext";
import { ReadjustmentPage } from "../pages/readjust";

export const AppRoutes = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();

	useEffect(() => {
		if (pathname.includes("//")) {
			const sanitizedPath = pathname.replace("//", "/");

			//console.log(sanitizedPath);
			navigate(sanitizedPath, { replace: true });
		}
	}, [pathname]);

	const { setDrawerOptions } = useDrawerContext();
	const { permissions, permissionLevel } = useProfile();

	useEffect(() => {
		if (permissionLevel) {
			const baseStructure: MenuItem[] = permissionLevel === 1 || permissionLevel === 2 ? garanttiMenuStructure : imobMenuStructure;
			const appliedRoleOptions = filterMenuByPermissions(baseStructure, permissions);
			setDrawerOptions(appliedRoleOptions);
		}
	}, [permissionLevel, permissions, setDrawerOptions]);



	return <Routes>{getRoutes()}</Routes>;
};

const getRoutes = () => {
	const { isSuperAdmin } = useProfile();

	return (
		<>
			<Route path="/login" element={<LoginPage />} />
			<Route path="/forgot-password" element={<ForgotPasswordPage />} />
			<Route path="/reset-password" element={<ResetPasswordPage />} />
			<Route
				path="/dashboard"
				element={
					<BannerProvider>
						<DashboardPage />
					</BannerProvider>
				}
			/>
			{checkByProfileGuard(PermissionRolesEnum.ChartsOptions, PermissionActionsEnum.View) && (
				<>
					<Route
						path="/dashboard/graficos"
						element={
							<BannerProvider>
								<DashboardGraphic />
							</BannerProvider>
						}
					/>
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.FireMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/incendio" element={<FirePage />} />
					<Route path="/incendio/orcamento" element={<FireQuotePage />} />
					<Route path="/incendio/orcamento/novo" element={<QuotationPage />} />
					<Route path="/incendio/orcamento/:id" element={<FireQuotePage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.UsersDashboard, PermissionActionsEnum.View) && (
				<>
					<Route path="/usuarios" element={<ListagemUsuariosPage />} />
					<Route path="/usuarios/detalhe/:id" element={<UsuariosDetalheGuard />} />
					<Route path="/usuarios/perfil/:id" element={<PerfilUserDetailPresentation />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.ClientsDashboard, PermissionActionsEnum.View) && (
				<>
					<Route path="/clientes" element={<ListagemClientesPage />} />

					<Route path="/clientes/detalheunico/:id" element={<ClientDetailPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Update) && (
				<>
					<Route path="/clientes/detalhe/:id" element={<DetalheClientesPage />} />
					<Route path="/clientes/detalheunico/:id" element={<ClientDetailPage />} />
					<Route path="/clientes/transferencia" element={<TransferClientsPage />} />
				</>
			)}
			{(checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.Contracts) ||
				checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts)) && (
				<>
					<Route path="/clientes/contratos" element={<ContratosListagemGuard />} />
					{(checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsDetails) ||
						checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts)) && (
						<Route path="/clientes/contratos/detalhes/:id" element={<ContratosDetalhesGuard />} />
					)}
					<Route path="/clientes/contratos/detalhe/:id" element={<ContratosDetalheGuard />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.AnalysisDashboard, PermissionActionsEnum.View) && (
				<>
					<Route path="/analises" element={<AnalysisListPage />} />
					<Route path="/analises/pedido" element={<AnalysisRequestPage />} />
					<Route path="/analises/pedido/:id" element={<AnalysisRequestPage />} />
					<Route path="/analises/contratacao" element={<AnalysisHiringPage />} />
					<Route path="/analises/visualizar/:id" element={<AnalysisDetailPage />} />
					<Route path="/analises/detalhe/:id" element={<AnalysisRequestPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.WarrantiesMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/garantias" element={<ProposalListPage />} />
					<Route path="/garantias/detalhe/:id" element={<ProposalDetailPage />} />
					<Route path="/garantias/visualizar/:id" element={<ProposalDetailPage />} />
					<Route path="/garantias/renovar/:id" element={<ProposalRenovatePage />} />
					<Route path="/garantias/alterar/:id" element={<ProposalEditPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.SinisterMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/sinistros" element={<SinisterListPage />} />
					<Route path="/sinistros/detalhe/:id" element={<SinisterDetailsPage />} />
					<Route path="/sinistros/valores/:id" element={<SinisterValuesPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.CreditRecoveryMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/sinistros/cobranca" element={<CreditRecoveryPage />} />
					<Route path="/sinistros/cobranca/detalhe/:id" element={<CreditRecoveryDetailsPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.AgreementsMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/sinistros/acordos" element={<AgreementsPage />} />
					<Route path="/sinistros/acordos/detalhe/:id" element={<AgreementsDetailsPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.CollectionControlMenu, PermissionActionsEnum.View) && (
				<Route path="/sinistros/controle-cobranca" element={<CollectionControlPage />} />
			)}
			{/* ####### R E P O R T S ####################### */}
			{checkByProfileGuard(PermissionRolesEnum.ReportsMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/relatorios" element={<ListagemRelatoriosPage />} />
					<Route path="/summary/financial" element={<FinancialSummaryPage />} />
					<Route path="/report/update-proposal" element={<ReportUpdateProposalPage />} />
					<Route path="/report/analysis" element={<ReportAnalysisPage />} />
					<Route path="/report/proposal" element={<ReportProposalPage />} />
					<Route path="/report" element={<ReportsPage />} />
					<Route path="/report/analysis/analytics" element={<ReportLifecycleAnalysisPage />} />
					<Route path="/report/proposal/analytics" element={<ReportLifecycleProposalPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.ReportsMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/bordero" element={<FinancialBorderoPage />} />
					<Route path="/saudefinanceira" element={<FinancialHealthListPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.FinancialMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/financeiro" element={<FinancialListPage />} />
					<Route path="/boletos" element={<FinancialInvoiceListPage />} />
					<Route path="/repasses" element={<FinancialPassPage />} />
					<Route path="/billet" element={<FinancialPage />} />
					<Route path="/notas" element={<NfseListPage />} />
					<Route path="/setup" element={<FinancialSetupPage />} />
								{checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.ReadjustView) && (
						<Route path="/reajuste" element={<ReadjustmentPage />} />
					)}
								{checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.BlockClient) && (
									<Route path="/bloqueio/cliente/:id" element={<ProccessCancelBlockClientPage />} />
								)}
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.GeneralConditionsMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/gerais" element={<ListagemGeraisPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.ChartsMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/graficos" element={<GraficosPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.SignatureMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/assinaturas" element={<SignaturePage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.PermissionsMenu, PermissionActionsEnum.View) && isSuperAdmin && (
				<>
					<Route path="/permissoes" element={<ListPermissionsPage />} />
					<Route path="/permissoes/detalhe/:id" element={<PermissionsDetailsPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.PeopleMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/blacklist" element={<PeoplePage />} />
					<Route path="/tools" element={<ToolsPage />} />
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.BannerMenu, PermissionActionsEnum.View) && (
				<>
					<Route
						path="/banners"
						element={
							<BannerProvider>
								<BannerManagementPage />
							</BannerProvider>
						}
					/>
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.CapitalizationMenu, PermissionActionsEnum.View) && (
				<>
					<Route path="/capitalizacao" element={<CapitalizationPage />} />
					{checkByProfileGuard(PermissionRolesEnum.CapitalizationOptions, PermissionActionsEnum.Add) && (
						<Route path="/capitalizacao/novo" element={<CapitalizationDetailPage />} />
					)}
					{checkByProfileGuard(PermissionRolesEnum.CapitalizationOptions, PermissionActionsEnum.Update) && (
						<Route path="/capitalizacao/editar/:id" element={<CapitalizationDetailPage />} />
					)}
				</>
			)}
			{checkByProfileGuard(PermissionRolesEnum.IntegratorMenu, PermissionActionsEnum.View) && (
				<Route path="/integrador" element={<IntegratorPage />} />
			)}
			<Route path="/installment/summary/:id" element={<SummaryPage />} />
			<Route path="/paymentinvoice/:id" element={<PaymentInvoicePage />} />
			<Route path="/sicoob/boleto/:id" element={<SicoobBoletoPage />} />
			<Route path="/" element={<Navigate replace to="/dashboard" />} />
			<Route path="*" element={<Navigate replace to="/dashboard" />} />
			<Route path="/onboard/payment/:id" element={<OnboardPaymentPage />} />
		</>
	);
};

const filterMenuByPermissions = (items: MenuItem[], permissions: PermissionRolesType | null | undefined): MenuItem[] => {
	return items.reduce<MenuItem[]>((menuAccumulator, currentItem) => {
		const filteredChildren = currentItem.children ? filterMenuByPermissions(currentItem.children, permissions) : undefined;
		const hasVisibleChildren = Boolean(filteredChildren && filteredChildren.length > 0);
		const isAllowed = hasMenuPermission(currentItem, permissions);

		if (!isAllowed && !(hasVisibleChildren && !currentItem.requiredRoleType)) {
			return menuAccumulator;
		}

		const nextItem: MenuItem = { ...currentItem };

		if (hasVisibleChildren) {
			nextItem.children = filteredChildren;
		} else {
			delete nextItem.children;
		}

		menuAccumulator.push(nextItem);
		return menuAccumulator;
	}, []);
};

const hasMenuPermission = (item: MenuItem, permissions: PermissionRolesType | null | undefined) => {
	if (!item.requiredRoleType) return true;
	if (!permissions) return false;

	const permissionData = permissions[item.requiredRoleType];
	if (permissionData?.view === true) return true;

	// Fallback legado: contratos costumavam ficar em clients_options.contracts
	if (item.requiredRoleType === PermissionRolesEnum.ContractsMenu) {
		const hasNewContractsMenuModule = Object.prototype.hasOwnProperty.call(permissions, PermissionRolesEnum.ContractsMenu);
		if (hasNewContractsMenuModule) return false;

		const legacyContracts = permissions[PermissionRolesEnum.ClientsOptions]?.[PermissionActionsEnum.Contracts];
		return legacyContracts === true;
	}

	return false;
};

